import { Suspense, useEffect } from 'react';
import { PageSkeleton } from '../common/PageSkeleton';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { firebaseEnabled } from '../../lib/firebase';
import { twoStepRequired } from '../../config/twoStep';
import { twoStepOn } from '../../services/twoStepService';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import { NEEDS_SERVER } from '../../services/userService';
import { Loader2 } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useBrandColor, useCurrentFacility, useCurrentUser } from '../../hooks/useAppData';
import { setDataScope } from '../../services/scope';
import { PoweredBy } from '../common/Brand';
import { BottomNav } from './BottomNav';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { SessionGuard } from './SessionGuard';
import { EmailCodeGate } from './EmailCodeGate';
import { TermContext } from '../../context/TermContext';
import { personTermFor } from '../../services/programmeService';

export function AppShell() {
  const { loggedIn, role, authReady } = useApp();
  const { data: facility } = useCurrentFacility();
  const { data: me } = useCurrentUser();
  if (me) setDataScope({ actor: { id: me.id, name: me.name, role: me.role } });
  useBrandColor(role === 'super_admin' ? undefined : facility?.brandColor);
  const location = useLocation();
  const t = useT();

  // Safety net: any save that fails (no internet, not allowed by the database rules, server-only action)
  // shows a clear message instead of silently doing nothing.
  useEffect(() => {
    const onFail = (e: PromiseRejectionEvent) => {
      const msg = e.reason instanceof Error ? e.reason.message : '';
      toast.error(msg === NEEDS_SERVER ? t('common.needsServer') : t('common.saveFailed'));
    };
    window.addEventListener('unhandledrejection', onFail);
    return () => window.removeEventListener('unhandledrejection', onFail);
  }, [t]);

  if (!authReady)
    return (
      <div className="flex min-h-screen items-center justify-center bg-sc-bg" data-testid="auth-loading">
        <Loader2 className="h-8 w-8 animate-spin text-sc-teal" />
      </div>
    );
  if (!loggedIn) return <Navigate to="/login" replace />;
  // Authenticator-app 2-step login (optional now; email codes below are required for everyone).
  if (firebaseEnabled && twoStepRequired(role) && !twoStepOn() && location.pathname !== '/two-step') return <Navigate to="/two-step?required=1" replace />;

  const shell = (
    <TermContext.Provider value={personTermFor(facility?.services)}>
    <div id="sc-app" className="flex min-h-screen bg-sc-bg">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col pb-20 lg:pb-0">
        <TopBar />
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-10 lg:py-8" data-testid="main-content">
          <Suspense fallback={<PageSkeleton />}><Outlet /></Suspense>
        </main>
        <footer className="flex justify-center px-4 py-5 lg:hidden">
          <PoweredBy />
        </footer>
      </div>
      <BottomNav />
    </div>
    <SessionGuard />
    </TermContext.Provider>
  );
  // 2-step login by email code, required for everyone in the real app.
  return firebaseEnabled ? <EmailCodeGate>{shell}</EmailCodeGate> : shell;
}
